import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateSectionRequest,
  CreateSubjectRequest,
  Section,
  SectionListResponse,
  Subject,
  SubjectListResponse,
  UpdateSectionRequest,
  UpdateSubjectRequest,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase G1 + Phase H4 — academic catalogue read + write hooks.
 *
 * The G1 read hooks power the first-run invite-staff teacher row
 * dropdowns. The H4 mutations power the new IA `/catalogue/subjects`
 * and `/catalogue/sections` DataTable pages.
 *
 * Both queries default to fetching active (non-archived) rows and are
 * paged generously enough (`limit=500` server-cap) to fit even a busy
 * K-12 tenant in one round trip — the IA admin almost never has more
 * than ~100 subjects or sections live at once. If we ever bump into
 * the limit we add a search-input + offset pagination later.
 *
 * Caching: subjects + sections are slow-moving by nature (created
 * once at tenant onboarding, occasionally edited). 5-minute stale
 * window keeps the wizard snappy without ever showing data older than
 * a coffee break. Mutations invalidate the matching read keys so the
 * catalogue page reflects writes without an explicit refetch.
 */

const SUBJECTS_KEY_ROOT = ["academic", "subjects"] as const;
const SECTIONS_KEY_ROOT = ["academic", "sections"] as const;

export function useSubjects(opts?: { includeArchived?: boolean; applicableGrade?: string }) {
  const includeArchived = opts?.includeArchived ?? false;
  const applicableGrade = opts?.applicableGrade ?? null;
  return useQuery<SubjectListResponse>({
    queryKey: [...SUBJECTS_KEY_ROOT, { includeArchived, applicableGrade }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (includeArchived) params.set("include_archived", "true");
      if (applicableGrade) params.set("applicable_grade", applicableGrade);
      params.set("limit", "500");
      return api.get<SubjectListResponse>(`/api/academic/subjects?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSections(opts?: {
  branchId?: string | null;
  gradeRef?: string | null;
  academicYearId?: string | null;
  includeArchived?: boolean;
}) {
  const branchId = opts?.branchId ?? null;
  const gradeRef = opts?.gradeRef ?? null;
  const academicYearId = opts?.academicYearId ?? null;
  const includeArchived = opts?.includeArchived ?? false;
  return useQuery<SectionListResponse>({
    queryKey: [...SECTIONS_KEY_ROOT, { branchId, gradeRef, academicYearId, includeArchived }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (branchId) params.set("branch_id", branchId);
      if (gradeRef) params.set("grade_ref", gradeRef);
      if (academicYearId) params.set("academic_year_id", academicYearId);
      if (includeArchived) params.set("include_archived", "true");
      params.set("limit", "500");
      return api.get<SectionListResponse>(`/api/academic/sections?${params.toString()}`);
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Subject mutations (Phase H4 FE-1) ───────────────────────────────

function invalidateSubjects(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: SUBJECTS_KEY_ROOT });
}

export function useCreateSubject() {
  const qc = useQueryClient();
  return useMutation<Subject, Error, CreateSubjectRequest>({
    mutationKey: [...SUBJECTS_KEY_ROOT, "create"],
    mutationFn: (body) => api.post<Subject>("/api/academic/subjects", body),
    onSuccess: () => invalidateSubjects(qc),
  });
}

export interface UseUpdateSubjectVars {
  id: string;
  body: UpdateSubjectRequest;
}

export function useUpdateSubject() {
  const qc = useQueryClient();
  return useMutation<Subject, Error, UseUpdateSubjectVars>({
    mutationKey: [...SUBJECTS_KEY_ROOT, "update"],
    mutationFn: ({ id, body }) => api.patch<Subject>(`/api/academic/subjects/${id}`, body),
    onSuccess: () => invalidateSubjects(qc),
  });
}

export function useArchiveSubject() {
  const qc = useQueryClient();
  return useMutation<Subject, Error, string>({
    mutationKey: [...SUBJECTS_KEY_ROOT, "archive"],
    // The BE uses the `:archive` action verb (idempotent) so a double-
    // click on a flaky network doesn't 404 the second attempt.
    mutationFn: (id) => api.post<Subject>(`/api/academic/subjects/${id}:archive`, {}),
    onSuccess: () => invalidateSubjects(qc),
  });
}

export function useUnarchiveSubject() {
  const qc = useQueryClient();
  return useMutation<Subject, Error, string>({
    mutationKey: [...SUBJECTS_KEY_ROOT, "unarchive"],
    mutationFn: (id) => api.post<Subject>(`/api/academic/subjects/${id}:unarchive`, {}),
    onSuccess: () => invalidateSubjects(qc),
  });
}

// ── Section mutations (Phase H4 FE-1) ───────────────────────────────

function invalidateSections(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: SECTIONS_KEY_ROOT });
}

export function useCreateSection() {
  const qc = useQueryClient();
  return useMutation<Section, Error, CreateSectionRequest>({
    mutationKey: [...SECTIONS_KEY_ROOT, "create"],
    mutationFn: (body) => api.post<Section>("/api/academic/sections", body),
    onSuccess: () => invalidateSections(qc),
  });
}

export interface UseUpdateSectionVars {
  id: string;
  body: UpdateSectionRequest;
}

export function useUpdateSection() {
  const qc = useQueryClient();
  return useMutation<Section, Error, UseUpdateSectionVars>({
    mutationKey: [...SECTIONS_KEY_ROOT, "update"],
    mutationFn: ({ id, body }) => api.patch<Section>(`/api/academic/sections/${id}`, body),
    onSuccess: () => invalidateSections(qc),
  });
}

export function useArchiveSection() {
  const qc = useQueryClient();
  return useMutation<Section, Error, string>({
    mutationKey: [...SECTIONS_KEY_ROOT, "archive"],
    mutationFn: (id) => api.post<Section>(`/api/academic/sections/${id}:archive`, {}),
    onSuccess: () => invalidateSections(qc),
  });
}

export function useUnarchiveSection() {
  const qc = useQueryClient();
  return useMutation<Section, Error, string>({
    mutationKey: [...SECTIONS_KEY_ROOT, "unarchive"],
    mutationFn: (id) => api.post<Section>(`/api/academic/sections/${id}:unarchive`, {}),
    onSuccess: () => invalidateSections(qc),
  });
}
